import React, {FC, useState} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../../src/assets/images/noResults.svg';
import {CrmSyncErrorsBannerContainer} from '../../../../../../../../src/components/accounts/crm/banners/crmSyncErrorsBannerContainer';
import {
  buildContactsManagerSyncsUrl,
  contactsLearnMoreUrl,
} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {globalNamespace} from '../../../../../../../../src/helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {SyncJobModel} from '../../../../../../../../src/models/api/jobModel';
import {getCompanyHasCrmSyncFeature} from '../../../../../../../../src/store/data/company/companySelectors';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CreateContactButtonContainer} from '../../common/buttons/createContactButtonContainer';
import {InfoButton} from '../../common/buttons/infoButton';
import {ContactsManagerImportPopover} from '../../common/contactsManagerImportPopover';
import {CrmSyncHeaderDetailsContainer} from '../../common/crmSyncHeaderDetailsContainer';
import {SharedContactsInfoBoxes} from '../../common/infobox/sharedContactsInfoBoxes';
import {VerticalRule} from '../../common/verticalRule';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {ImportContactsCSVDialogContainer} from '../../dialogs/import/contacts/importContactsCSVDialogContainer';
import {ExportContactsPopoverContainer} from '../../exportContacts/exportContactsPopoverContainer';
import {buildInfoBoxToggleLabel} from '../../helpers/infoBoxHelpers';
import {ContactsTableMultiSelectActions} from '../../tables/common/contacts/contactsTableMultiSelectActions';
import {ContactsManagerTableEmptyState} from '../../tables/common/contactsManagerEmptyState';
import {ContactsTableContainer} from '../../tables/contacts/contactsTableContainer';

/*
 * Props.
 */

interface SharedContactsPageProps {
  isShowingImportCSVDialog: boolean;
  hasRunningImportContactsCSVJobs: boolean;
  isInfoBoxExpanded: boolean;
  hasCrmSyncFullPageSalesforce: boolean;
  hasHubspotContactSync: boolean;
  hasOutlookDirectorySync: boolean;
  selectedCardIds: ReadonlyArray<number>;
  activeSyncJobs: ReadonlyArray<SyncJobModel>;
  crmSyncs: ReadonlyArray<CrmSyncModel>;
  isCrmSyncConnectedById: {[crmSyncId: number]: boolean | undefined};
  onToggleInfoBox: Handler;
  onToggleImportCSVClick: Handler;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledCrmSyncErrorsWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 16px 48px 16px 45px;
  &:empty {
    padding: 0;
  }
`;

const StyledContentWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
`;

const StyledImg = styled.img`
  width: 92px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  height: inherit;
`;

const StyledContextDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

const StyledCrmSyncErrorsBannerContainer = styled(CrmSyncErrorsBannerContainer)`
  border-radius: 18px;
`;

/*
 * Component.
 */

export const SharedContactsPage: FC<SharedContactsPageProps> = (props) => {
  const {
    selectedCardIds,
    isInfoBoxExpanded,
    hasCrmSyncFullPageSalesforce,
    hasHubspotContactSync,
    hasOutlookDirectorySync,
    hasRunningImportContactsCSVJobs,
    isShowingImportCSVDialog,
    activeSyncJobs,
    crmSyncs,
    isCrmSyncConnectedById,
    navigateToLink,
    onToggleInfoBox,
    onToggleImportCSVClick,
  } = props;
  const {panelId} = useContactsManagerContext();
  const hasCrmSyncFeature = useAppSelector(getCompanyHasCrmSyncFeature);
  const [hiddenCrmSyncErrorBannerIds, setHiddenCrmSyncErrorBannerIds] = useState<ReadonlyArray<number>>([]);

  const onImportSalesforceClick = () => {
    navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
  };

  const onImportHubspotClick = () => {
    navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.HUBSPOT));
  };

  const onImportOutlookDirectoryClick = () =>
    navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.OUTLOOK_DIRECTORY));

  const onHideCrmSyncErrorBanner = (syncId: number) => {
    setHiddenCrmSyncErrorBannerIds((syncIds) => [...syncIds, syncId]);
  };

  const renderContactsManagerImportPopover = () => (
    <ContactsManagerImportPopover
      isDisabled={hasRunningImportContactsCSVJobs}
      onImportCSVClick={onToggleImportCSVClick}
      hasCrmSyncFeature={hasCrmSyncFeature}
      onImportSalesforceClick={hasCrmSyncFullPageSalesforce ? onImportSalesforceClick : undefined}
      onImportHubspotClick={hasHubspotContactSync ? onImportHubspotClick : undefined}
      onImportOutloookDirectoryClick={hasOutlookDirectorySync ? onImportOutlookDirectoryClick : undefined}
    />
  );

  const shouldShowErrorBanner = (crmSync?: CrmSyncModel) => {
    if (
      !crmSync ||
      crmSync.status === CrmSyncStatusesEnum.DISABLED ||
      !isCrmSyncConnectedById[crmSync.id] ||
      hiddenCrmSyncErrorBannerIds.includes(crmSync.id)
    ) {
      return false;
    }
    const hasSalesforceSync =
      crmSync.source === CrmSyncSourcesEnum.SALESFORCE && hasCrmSyncFullPageSalesforce;
    const hasHubspotSync = crmSync.source === CrmSyncSourcesEnum.HUBSPOT && hasHubspotContactSync;
    const hasOutlookSync = crmSync.source === CrmSyncSourcesEnum.OUTLOOK_DIRECTORY && hasOutlookDirectorySync;
    return hasSalesforceSync || hasHubspotSync || hasOutlookSync;
  };

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="shared-contacts-table-title">Shared contacts</Trans>}
        contextDetails={maybeRenderContextDetails(panelId, activeSyncJobs)}
        actions={
          <>
            <ExportContactsPopoverContainer />
            <CreateContactButtonContainer />
            {renderContactsManagerImportPopover()}
            <VerticalRule />
            <InfoButton
              label={buildInfoBoxToggleLabel()}
              onToggle={onToggleInfoBox}
              isActive={isInfoBoxExpanded}
            />
          </>
        }
      />
      <StyledWrapperDiv>
        <StyledContentWrapperDiv>
          <StyledCrmSyncErrorsWrapperDiv>
            {crmSyncs.map((crmSync) => {
              if (!shouldShowErrorBanner(crmSync)) {
                return null;
              }

              return (
                <StyledCrmSyncErrorsBannerContainer
                  key={crmSync.id}
                  syncId={crmSync.id}
                  onViewErrorsClick={() => navigateToLink(buildContactsManagerSyncsUrl(crmSync.source, true))}
                  onCloseClick={() => onHideCrmSyncErrorBanner(crmSync.id)}
                  shouldDisplayCrmSyncName
                />
              );
            })}
          </StyledCrmSyncErrorsWrapperDiv>
          <ContactsTableContainer
            panelId={panelId}
            renderEmpty={() => renderEmptyState(renderContactsManagerImportPopover)}
            queryOptions={{namespace: globalNamespace}}
            multiSelectHeaderActions={
              <ContactsTableMultiSelectActions
                panelId={panelId}
                isAddToListsButtonVisible
                cardIds={selectedCardIds}
              />
            }
          />
        </StyledContentWrapperDiv>

        {isInfoBoxExpanded && <SharedContactsInfoBoxes />}
      </StyledWrapperDiv>
      {isShowingImportCSVDialog && (
        <ImportContactsCSVDialogContainer panelId={panelId} onRequestClose={onToggleImportCSVClick} />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderContextDetails(
  panelId: ContactsManagerPanelId,
  activeSyncJobs: ReadonlyArray<SyncJobModel>,
) {
  return (
    <StyledContextDetailsWrapperDiv>
      <CrmSyncHeaderDetailsContainer
        resourceType={CrmSyncIntegrationResourceTypesEnum.CARD}
        panelId={panelId}
        activeSyncJobs={activeSyncJobs}
      />
    </StyledContextDetailsWrapperDiv>
  );
}

function renderEmptyState(renderContactsManagerImportPopover: Renderer) {
  return (
    <ContactsManagerTableEmptyState
      title={<Trans id="shared-contacts-table-empty-state-title">No shared contacts</Trans>}
      image={<StyledImg alt="shared-contacts-table-empty-state-image" src={noResultsImageSrc} />}
      description={
        <Trans id="shared-contacts-table-empty-state-description">
          Shared Contacts allow you to manage contact information that is shared across your entire company
          within Front.
        </Trans>
      }
      learnMoreUrl={contactsLearnMoreUrl}
    >
      <CreateContactButtonContainer />
      {renderContactsManagerImportPopover()}
    </ContactsManagerTableEmptyState>
  );
}
